import {graphql} from '~/graphql-react-query/';

export const ExpenditureReportV3 = graphql(`
    query MediaValueRowsV3(
        $columns: [MediaValueInputColumn!]
        $columnFilter: MediaValueColumnFilter
        $excludeFilter: MediaValueExcludeList
        $dateRangeFilter: MediaValueDateRangeFilter
        $pagination: MediaValuePagination
        $changesetId: Int
        $adType: Boolean!
        $brand: Boolean!
        $campaign: Boolean!
        $category: Boolean!
        $industry: Boolean!
        $mediaOwner: Boolean!
        $mediaType: Boolean!
        $product: Boolean!
        $publisher: Boolean!
        $region: Boolean!
        $segment: Boolean!
        $publication: Boolean!
    ) {
        mediaValue {
            rowsV3(
                columns: $columns
                columnFilter: $columnFilter
                dateRangeFilter: $dateRangeFilter
                pagination: $pagination
                excludeList: $excludeFilter
                changesetId: $changesetId
            ) {
                rows {
                    date
                    value
                    adType @include(if: $adType)
                    brand @include(if: $brand)
                    campaign @include(if: $campaign)
                    category @include(if: $category)
                    industry @include(if: $industry)
                    mediaOwner @include(if: $mediaOwner)
                    mediaType @include(if: $mediaType)
                    product @include(if: $product)
                    publisher @include(if: $publisher)
                    region @include(if: $region)
                    segment @include(if: $segment)
                    publication @include(if: $publication)
                }
                labels {
                    segment @include(if: $segment) {
                        id
                        name
                        color
                    }
                }
                columnTotals {
                    column
                    value
                }
                grandTotal
                pageInfo {
                    currentPage
                    hasNextPage
                    hasPreviousPage
                }
                meta {
                    isTruncated
                }
            }
        }
    }
`);

export const AustralianMediaValueCsv = graphql(`
    query MediaValueCsvV3(
        $columns: [MediaValueInputColumn!]
        $columnFilter: MediaValueColumnFilter
        $dateRangeFilter: MediaValueDateRangeFilter
        $csvFileName: String
        $pagination: MediaValuePagination
        $changesetId: Int
        $includePercentage: Boolean
        $includeDollarFigureForSOVReports: Boolean
    ) {
        mediaValue {
            csvV3(
                columns: $columns
                columnFilter: $columnFilter
                dateRangeFilter: $dateRangeFilter
                csvFileName: $csvFileName
                pagination: $pagination
                changesetId: $changesetId
                includePercentage: $includePercentage
                includeDollarFigureForSOVReports: $includeDollarFigureForSOVReports
            ) {
                csv
            }
        }
    }
`);

export const GlobalMediaValueCsv = graphql(`
    query gmvExpenditureReportCSVExport(
        $columns: [GlobalMediaValueInputColumn!]
        $columnFilter: GlobalMediaValueColumnFilter
        $dateRangeFilter: GlobalMediaValueDateRangeFilter
        $csvFileName: String
        $pagination: GlobalMediaValuePagination
        $country: SupportedCountry!
        $includePercentage: Boolean
        $includeDollarFigureForSOVReports: Boolean
    ) {
        globalMediaValue {
            csv(
                country: $country
                columns: $columns
                columnFilter: $columnFilter
                dateRangeFilter: $dateRangeFilter
                csvFileName: $csvFileName
                pagination: $pagination
                includePercentage: $includePercentage
                includeDollarFigureForSOVReports: $includeDollarFigureForSOVReports
            ) {
                csv
            }
        }
    }
`);

export const Distribution = graphql(`
    query DistributionV3(
        $dateRangeFilter: MediaValueDateRangeFilter!
        $breakdownColumn: MediaValueBreakdownColumn!
        $rows: [MediaValueRowFilterInput!]!
    ) {
        mediaValue {
            distributionV3(
                dateRangeFilter: $dateRangeFilter
                breakdownColumn: $breakdownColumn
                rows: $rows
            ) {
                rows
                columnMeta
            }
        }
    }
`);

export const GlobalMvDistribution = graphql(`
    query gmvDistribution(
        $dateRangeFilter: GlobalMediaValueDateRangeFilter!
        $breakdownColumn: GlobalMediaValueBreakdownColumn!
        $rows: [GlobalMediaValueRowFilterInput!]!
        $country: SupportedCountry!
    ) {
        globalMediaValue {
            distribution(
                country: $country
                dateRangeFilter: $dateRangeFilter
                breakdownColumn: $breakdownColumn
                rows: $rows
            ) {
                rows
                columnMeta
            }
        }
    }
`);

export const Comparison = graphql(`
    query ComparisonV3($periodSelection: PeriodSelection!, $rows: [MediaValueRowFilterInput!]!) {
        mediaValue {
            comparisonV3(periodSelection: $periodSelection, rows: $rows) {
                rows
                columnMeta
            }
        }
    }
`);

export const GlobalComparison = graphql(`
    query gmvComparison(
        $periodSelection: GlobalMediaValuePeriodSelection!
        $rows: [GlobalMediaValueRowFilterInput!]!
        $country: SupportedCountry!
    ) {
        globalMediaValue {
            comparison(periodSelection: $periodSelection, rows: $rows, country: $country) {
                rows
                columnMeta
            }
        }
    }
`);

export const GetForecastDates = graphql(`
    query GetForecastDates($queryOpensearch: Boolean) {
        mediaValue {
            getForecastDates(queryOpensearch: $queryOpensearch) {
                forecastCutoff
                forecastCutoffDigital
                maxDate
            }
        }
    }
`);

export const GmvStatsQuery = graphql(`
    query gmvStatsQuery($country: SupportedCountry!) {
        globalMediaValue {
            stats(country: $country) {
                latestDate
            }
        }
    }
`);

export const GmvExpenditureReport = graphql(`
    query gmvExpenditureReport(
        $country: SupportedCountry!
        $columns: [GlobalMediaValueInputColumn!]
        $columnFilter: GlobalMediaValueColumnFilter
        $excludeFilter: GlobalMediaValueExcludeList
        $dateRangeFilter: GlobalMediaValueDateRangeFilter
        $pagination: GlobalMediaValuePagination
        $includeColumnTotals: Boolean!
        $adType: Boolean!
        $brand: Boolean!
        $category: Boolean!
        $industry: Boolean!
        $mediaType: Boolean!
        $product: Boolean!
        $publication: Boolean!
        $region: Boolean!
    ) {
        globalMediaValue {
            expenditureReport(
                country: $country
                columnFilter: $columnFilter
                columns: $columns
                dateRangeFilter: $dateRangeFilter
                excludeList: $excludeFilter
                pagination: $pagination
                includeColumnTotals: $includeColumnTotals
            ) {
                cells {
                    date
                    value

                    adType @include(if: $adType)
                    brand @include(if: $brand)
                    category @include(if: $category)
                    industry @include(if: $industry)
                    mediaType @include(if: $mediaType)
                    product @include(if: $product)
                    publication @include(if: $publication)
                    region @include(if: $region)
                }
                columnTotals @include(if: $includeColumnTotals) {
                    column
                    value
                }
                grandTotal @include(if: $includeColumnTotals)
                pageInfo {
                    currentPage
                    hasNextPage
                    hasPreviousPage
                }
                meta {
                    isTruncated
                }
            }
        }
    }
`);
